import { formatDistanceToNow } from 'date-fns';
export const filterDemoProfilesPage = (profiles, query) => {
    const q = query.trim().toLowerCase();
    if (!q)
        return profiles;
    return profiles.filter((p) => p.email.toLowerCase().includes(q) ||
        p.first_name.toLowerCase().includes(q) ||
        p.last_name.toLowerCase().includes(q));
};
export const filterProspectDetailsByType = (prospectDetails, prospectType) => prospectType == null
    ? prospectDetails
    : prospectDetails.filter((p) => p.prospect_type === prospectType);
// Keep aligned with hero-notification-api mark/segments.py ADMIN_ROLES.
const MEMBERSHIP_ADMIN_ROLES = new Set([
    'primary_user_admin',
    'caregiver_admin',
    'membership_admin'
]);
export const filterMembershipsByTargetKind = (memberships, targetKind) => targetKind === 'MEMBERSHIP_ADMIN'
    ? memberships.filter((membership) => membership.roles.some(({ role }) => MEMBERSHIP_ADMIN_ROLES.has(role)))
    : memberships;
export const dripHasRunnableVersion = (drip) => (drip.current_version?.steps.length ?? 0) > 0 || (drip.draft_version?.steps.length ?? 0) > 0;
// Demo enroll runs against active_version if present, otherwise draft_version.
// An ACTIVE/PAUSED drip with a pending draft can only be tested against the
// live side — the endpoint has no way to preview the draft.
export const pendingDraftBlocksTest = (drip) => (drip.status === 'ACTIVE' || drip.status === 'PAUSED') && drip.draft_version != null;
export const evaluateEnrollGate = (drip, selectedProfileId, contact, tuple) => {
    if (selectedProfileId == null) {
        return { allowed: false, reason: 'Select a demo profile first.' };
    }
    // Belt-and-braces: the detail query is keyed by profile_id, so a response
    // for a previously selected profile can never land under this key — this
    // check only guards against this function being called with mismatched
    // arguments directly (e.g. from a test), not a real cache race.
    if (contact == null || contact.profile_id !== selectedProfileId) {
        return { allowed: false, reason: 'Loading profile details…' };
    }
    if (!contact.is_demo) {
        return { allowed: false, reason: 'This profile is not marked as a demo profile.' };
    }
    if (contact.is_placeholder) {
        return { allowed: false, reason: 'This profile is a placeholder and cannot be enrolled.' };
    }
    if (!dripHasRunnableVersion(drip)) {
        return { allowed: false, reason: 'This drip has no steps to run yet.' };
    }
    if (drip.target_kind === 'MEMBERSHIP' || drip.target_kind === 'MEMBERSHIP_ADMIN') {
        // Not just non-null — the id must still name a membership on the
        // freshly returned detail (it may have come from a picker render
        // for a prior profile, or the membership may no longer exist).
        const eligibleMemberships = filterMembershipsByTargetKind(contact.memberships, drip.target_kind);
        const membershipStillExists = tuple.membershipId != null &&
            eligibleMemberships.some((m) => m.membership_id === tuple.membershipId);
        if (!membershipStillExists) {
            if (drip.target_kind === 'MEMBERSHIP_ADMIN') {
                return {
                    allowed: false,
                    reason: eligibleMemberships.length === 0
                        ? 'This profile has no membership with an admin role.'
                        : 'Select a membership where this profile has an admin role.'
                };
            }
            return { allowed: false, reason: 'Select a membership for this profile.' };
        }
    }
    if (drip.target_kind === 'PROSPECT') {
        const matching = filterProspectDetailsByType(contact.prospect_details, drip.trigger_type === 'PROSPECT' ? drip.prospect_type : null);
        if (matching.length === 0) {
            return {
                allowed: false,
                reason: `No ${drip.prospect_type ?? 'matching'} prospect on this profile.`
            };
        }
        if (tuple.prospectDetailsId == null) {
            return { allowed: false, reason: 'Select a prospect record for this profile.' };
        }
        if (!matching.some((p) => p.prospect_details_id === tuple.prospectDetailsId)) {
            return {
                allowed: false,
                reason: 'The selected prospect record is no longer available. Select another prospect record.'
            };
        }
    }
    return { allowed: true, reason: null };
};
export const buildEnrollBody = (drip, profileId, tuple) => {
    const body = { drip_id: drip.drip_id, profile_id: profileId };
    if ((drip.target_kind === 'MEMBERSHIP' || drip.target_kind === 'MEMBERSHIP_ADMIN') &&
        tuple.membershipId != null) {
        body.membership_id = tuple.membershipId;
    }
    if (drip.target_kind === 'PROSPECT' && tuple.prospectDetailsId != null) {
        body.prospect_details_id = tuple.prospectDetailsId;
    }
    return body;
};
// Reset deletes every DripInstance row for the (drip, profile) pair — every
// local row for that profile must be marked reset, not only the clicked one.
export const markInstancesReset = (instances, profileId) => instances.map((instance) => instance.profile_id === profileId ? { ...instance, reset: true } : instance);
// One error map, keyed by drip_instance_id, shared by both actions — a row
// can only ever show its most recent outcome. Starting either action clears
// whatever was there first, so a stale error from the OTHER action can never
// outlive or outrank a fresh result.
export const clearRowErrors = (errors, ids) => {
    let changed = false;
    const next = new Map(errors);
    ids.forEach((id) => {
        if (next.delete(id))
            changed = true;
    });
    return changed ? next : errors;
};
export const markRowErrors = (errors, ids, message) => {
    const next = new Map(errors);
    ids.forEach((id) => next.set(id, message));
    return next;
};
export const applyFastForwardResult = (instances, response) => instances.map((instance) => instance.drip_instance_id === response.drip_instance_id
    ? { ...instance, current_step: response.current_step, due_at: response.due_at }
    : instance);
export const formatDueAt = (dueAt) => {
    if (!dueAt)
        return '—';
    const date = new Date(dueAt);
    if (Number.isNaN(date.getTime()))
        return '—';
    return formatDistanceToNow(date, { addSuffix: true });
};
const stripTrailingSlash = (pathname) => pathname.replace(/\/+$/, '');
// React Router matches this route with or without a trailing slash, so a
// deep link to .../test/ still renders the Test tab — strip it before
// comparing so this helper agrees with what actually rendered.
export const isTestTabPath = (pathname) => stripTrailingSlash(pathname).endsWith('/test');
// Same trailing-slash tolerance for "is the browser already at this exact
// tab path" checks — without it, re-clicking the active tab while the URL
// has a trailing slash (e.g. a deep link to .../test/) looks like a real
// change and pushes a redundant history entry for the canonical path.
export const isAtPath = (pathname, targetPath) => stripTrailingSlash(pathname) === targetPath;
export const detailBasePath = (dripId) => `/mark/drips/${dripId}`;
export const testTabPath = (dripId) => `${detailBasePath(dripId)}/test`;
